import { useState } from 'react';
import { Badge } from '@/components/ui/badge.js';

type AboutTab = 'how-it-works' | 'glossary' | 'roles' | 'rdti-primer';

const TABS: { key: AboutTab; label: string }[] = [
  { key: 'how-it-works', label: 'How it Works' },
  { key: 'glossary', label: 'Glossary' },
  { key: 'roles', label: 'Roles' },
  { key: 'rdti-primer', label: 'R&DTI Primer' },
];

const PROCESS_STEPS = [
  {
    n: 1,
    label: 'Signal Sources',
    description: 'GitHub commits, transcripts, and LLM sessions flow in automatically',
  },
  {
    n: 2,
    label: 'Artefacts',
    description: 'Each signal becomes a timestamped, immutable evidence artefact',
  },
  {
    n: 3,
    label: 'TFA Lock',
    description: 'A panel of competent assessors locks the scope of eligible R&D',
  },
  {
    n: 4,
    label: 'Classification',
    description: 'Timewriting sessions are classified against the locked TFA scope by AI',
  },
  {
    n: 5,
    label: 'Claim',
    description: 'Approved time and expenditure are assembled into a defensible claim',
  },
  {
    n: 6,
    label: 'Audit Portal',
    description: 'Tax advisors and auditors review evidence via a scoped access link',
  },
];

const GLOSSARY: { term: string; definition: string }[] = [
  {
    term: 'Abandoned Work',
    definition:
      'Engineering effort that was started but not completed — a strong indicator of genuine technical uncertainty, automatically detected from branch deletions and stale PRs.',
  },
  {
    term: 'Activity',
    definition:
      'A discrete unit of R&D work under a project, each with an IP ownership designation and a competent assessor.',
  },
  {
    term: 'Artefact',
    definition:
      'A piece of evidence — a commit, transcript, meeting note, or document — captured contemporaneously and stored immutably.',
  },
  {
    term: 'Audit Log',
    definition:
      'An immutable, hash-chained record of every action taken in Edison, suitable for ATO and AusIndustry audit review.',
  },
  {
    term: 'Bucket',
    definition:
      'A cluster of related work items (commits, sessions) within a time entry, used to attribute hours between R&D and non-R&D work.',
  },
  {
    term: 'Claim',
    definition:
      'The formal R&DTI claim record — assembles approved activities, timewriting, and expenditure for lodgement.',
  },
  {
    term: 'Competent Assessor',
    definition:
      'A person with relevant qualifications and expertise who assesses the technical uncertainty of an R&D activity. Their role and reasoning must be in their own words.',
  },
  {
    term: 'Core Activity',
    definition:
      'An R&D activity that involves genuine experimental work to resolve technical uncertainty. Directly claimable.',
  },
  {
    term: 'Evidence',
    definition:
      'The collective body of artefacts linked to a project or activity that supports the R&DTI claim.',
  },
  {
    term: 'R&DTI',
    definition:
      'The Australian Research and Development Tax Incentive — a tax offset under Division 355 of the Income Tax Assessment Act 1997 for companies conducting eligible R&D.',
  },
  {
    term: 'Signal Source',
    definition:
      'A connected data source (e.g., a GitHub repository) that automatically generates artefacts as engineering work happens.',
  },
  {
    term: 'Supporting Activity',
    definition:
      'An activity that is directly connected to, and necessary for, a core R&D activity. Claimable only when the core activity is eligible.',
  },
  {
    term: 'TFA (Technical Feasibility Assessment)',
    definition:
      'A five-stage panel process that documents the technical uncertainty of a project and locks the scope of eligible R&D. Classification cannot begin until the TFA is locked.',
  },
  {
    term: 'TFA Lock',
    definition:
      'The point at which the TFA panel reaches a determination. After locking, activities can be created and timewriting can be classified.',
  },
  {
    term: 'Time Entry',
    definition:
      'A recorded period of work by a team member, classified as R&D or non-R&D against a locked TFA.',
  },
];

const CAPABILITIES = [
  {
    label: 'View projects, evidence, claims',
    viewer: true,
    member: true,
    manager: true,
    admin: true,
  },
  {
    label: 'Log timewriting',
    viewer: false,
    member: true,
    manager: true,
    admin: true,
  },
  {
    label: 'Create and edit artefacts',
    viewer: false,
    member: true,
    manager: true,
    admin: true,
  },
  {
    label: 'Create activities, manage TFA',
    viewer: false,
    member: false,
    manager: true,
    admin: true,
  },
  {
    label: 'Lock TFA, lock claims',
    viewer: false,
    member: false,
    manager: true,
    admin: true,
  },
  {
    label: 'Issue audit portal access tokens',
    viewer: false,
    member: false,
    manager: true,
    admin: true,
  },
  {
    label: 'Manage users and roles',
    viewer: false,
    member: false,
    manager: false,
    admin: true,
  },
  {
    label: 'Bulk-approve timewriting',
    viewer: false,
    member: false,
    manager: true,
    admin: true,
  },
];

function HowItWorksTab() {
  return (
    <div className="space-y-8">
      {/* Process flow */}
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
          Process Flow
        </h2>

        {/* Desktop: horizontal chain */}
        <div className="hidden md:flex items-start gap-0">
          {PROCESS_STEPS.map((step, i) => (
            <div key={step.n} className="flex items-start flex-1 min-w-0">
              <div className="rounded-lg border bg-card p-4 flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-muted-foreground">{step.n}</span>
                  <span className="text-sm font-medium truncate">{step.label}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
              {i < PROCESS_STEPS.length - 1 && (
                <div className="flex items-center px-2 pt-5 text-muted-foreground shrink-0 text-lg">
                  ›
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile: vertical stack */}
        <div className="flex md:hidden flex-col gap-0">
          {PROCESS_STEPS.map((step, i) => (
            <div key={step.n} className="flex flex-col items-center">
              <div className="rounded-lg border bg-card p-4 w-full">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-muted-foreground">{step.n}</span>
                  <span className="text-sm font-medium">{step.label}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
              {i < PROCESS_STEPS.length - 1 && (
                <div className="text-muted-foreground py-1 text-lg">↓</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Prose */}
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          How It Works
        </h2>
        <p className="text-sm text-foreground leading-relaxed max-w-2xl">
          Edison captures engineering evidence continuously throughout the income year — not at tax
          time. As your team works, commits, transcripts, and session logs are automatically ingested
          as immutable artefacts. A panel of competent assessors locks the scope of eligible R&D via
          the Technical Feasibility Assessment, after which timewriting is classified against that
          locked scope by AI. The resulting evidence chain — from raw signals through to a locked
          claim — is designed to satisfy AusIndustry contemporaneity requirements and withstand audit review.
        </p>
      </div>
    </div>
  );
}

function GlossaryTab() {
  return (
    <div>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
        Terminology
      </h2>
      <dl className="space-y-4 max-w-2xl">
        {GLOSSARY.map(({ term, definition }) => (
          <div key={term} className="border-b pb-4 last:border-b-0 last:pb-0">
            <dt className="text-sm font-medium text-primary mb-1">{term}</dt>
            <dd className="text-sm text-muted-foreground pl-3 leading-relaxed">{definition}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function RolesTab() {
  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-muted-foreground leading-relaxed">
        Edison has four roles in descending privilege order. All roles can read evidence and view the
        audit log; write access varies by role.
      </p>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground font-normal">
                Capability
              </th>
              <th className="px-3 py-3 text-center">
                <Badge variant="default">Viewer</Badge>
              </th>
              <th className="px-3 py-3 text-center">
                <Badge variant="default">Member</Badge>
              </th>
              <th className="px-3 py-3 text-center">
                <Badge variant="info">R&D Manager</Badge>
              </th>
              <th className="px-3 py-3 text-center">
                <Badge variant="positive">Admin</Badge>
              </th>
            </tr>
          </thead>
          <tbody>
            {CAPABILITIES.map((cap) => (
              <tr key={cap.label} className="border-t">
                <td className="px-4 py-3 text-sm text-foreground">{cap.label}</td>
                <td className="px-3 py-3 text-center text-muted-foreground">
                  {cap.viewer ? '✓' : '—'}
                </td>
                <td className="px-3 py-3 text-center text-muted-foreground">
                  {cap.member ? '✓' : '—'}
                </td>
                <td className="px-3 py-3 text-center text-muted-foreground">
                  {cap.manager ? '✓' : '—'}
                </td>
                <td className="px-3 py-3 text-center text-muted-foreground">
                  {cap.admin ? '✓' : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RdtiPrimerTab() {
  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          What is Division 355
        </h2>
        <p className="text-sm text-foreground leading-relaxed">
          Division 355 of the Income Tax Assessment Act 1997 provides a federal tax offset for
          Australian companies conducting eligible R&D activities. There are two streams: refundable
          (for companies with aggregated turnover below $20M, providing a cash refund) and
          non-refundable (for larger companies, applied as a tax offset). Companies must self-assess
          eligibility, register their R&D activities with AusIndustry annually, and maintain
          contemporaneous records to support their claims.
        </p>
      </div>

      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Core vs Supporting Activities
        </h2>
        <p className="text-sm text-foreground leading-relaxed">
          Core activities involve genuine experimental work — a systematic progression of work based
          on the principles of established science, where the outcome could not be known in advance
          by a competent professional in the field. Supporting activities are directly connected to,
          and necessary for, the conduct of a core R&D activity. Both are claimable; supporting
          activities cannot be claimed without a corresponding eligible core activity.
        </p>
      </div>

      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          What Makes a Claim Defensible
        </h2>
        <p className="text-sm text-foreground leading-relaxed">
          A defensible R&DTI claim rests on four pillars: contemporaneous evidence captured during
          the work (not reconstructed afterwards); a locked TFA documenting the technical uncertainty
          before or as the work begins; competent assessors assessing uncertainty in their own words;
          and a clear, traceable chain from raw evidence to activity to claim. Edison's immutable
          artefact store, hash-chained audit log, and TFA lock gate are designed to satisfy all four
          requirements.
        </p>
      </div>

      <p className="text-xs text-muted-foreground border-t pt-4">
        Edison provides information only. This is not legal or tax advice. Engage a registered tax
        agent or R&DTI consultant for advice specific to your circumstances.
      </p>
    </div>
  );
}

export function AboutPage() {
  const [activeTab, setActiveTab] = useState<AboutTab>('how-it-works');

  return (
    <div className="space-y-0">
      {/* ── Sticky header ─────────────────────────────────────── */}
      <div className="sticky top-0 z-10 bg-background border-b pb-0">
        <div className="px-1 pt-1 pb-3">
          <h1 className="text-2xl font-semibold tracking-tight">About Edison</h1>
          <p className="text-sm text-muted-foreground mt-1">
            How Edison works and the R&DTI framework it supports.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ───────────────────────────────────────── */}
      <div className="pt-5">
        {activeTab === 'how-it-works' && <HowItWorksTab />}
        {activeTab === 'glossary' && <GlossaryTab />}
        {activeTab === 'roles' && <RolesTab />}
        {activeTab === 'rdti-primer' && <RdtiPrimerTab />}
      </div>
    </div>
  );
}
